import type { RegionId } from './types';

export type ReceptorId =
  // Adenosine
  | 'A1'
  | 'A2A'
  // Amino acid / ion channel
  | 'GABA_A'
  | 'GABA_B'
  | 'NMDA'
  | 'NAV'
  // Cholinergic
  | 'nAChR'
  | 'nAChR_MUSCLE'
  | 'M1'
  | 'M2'
  // Monoamine receptors
  | '5HT1A'
  | '5HT2A'
  | '5HT2C'
  | '5HT3'
  | 'D1'
  | 'D2'
  | 'D3'
  | 'ALPHA1'
  | 'ALPHA2'
  | 'BETA1'
  | 'BETA2'
  | 'H1'
  // Monoamine transporters
  | 'SERT'
  | 'DAT'
  | 'NET'
  // Opioid
  | 'MOR'
  | 'KOR'
  | 'DOR'
  // Cannabinoid
  | 'CB1'
  | 'CB2'
  // Melatonin
  | 'MT1'
  | 'MT2'
  // Nuclear
  | 'GR';

export interface ReceptorInfo {
  id: ReceptorId;
  displayName: string;
  description: string;
}

export const RECEPTORS: Record<ReceptorId, ReceptorInfo> = {
  A1: {
    id: 'A1',
    displayName: 'Adenosine A1',
    description: 'Inhibitory GPCR; blockade reduces sleepiness signalling',
  },
  A2A: {
    id: 'A2A',
    displayName: 'Adenosine A2A',
    description: 'Dense in striatum; blockade increases arousal',
  },
  GABA_A: {
    id: 'GABA_A',
    displayName: 'GABA-A',
    description: 'Main inhibitory ion channel receptor in the brain',
  },
  GABA_B: {
    id: 'GABA_B',
    displayName: 'GABA-B',
    description: 'Slow inhibitory GPCR; muscle relaxation and sedation',
  },
  NMDA: {
    id: 'NMDA',
    displayName: 'NMDA (glutamate)',
    description: 'Excitatory ion channel; central to learning and memory',
  },
  NAV: {
    id: 'NAV',
    displayName: 'Voltage-gated sodium channel',
    description: 'Carries the action potential; blocking it silences nerves locally',
  },
  nAChR: {
    id: 'nAChR',
    displayName: 'Nicotinic ACh (neuronal α4β2)',
    description: 'Ligand-gated channel; nicotine reward, alertness, ganglionic relay',
  },
  nAChR_MUSCLE: {
    id: 'nAChR_MUSCLE',
    displayName: 'Nicotinic ACh (muscle)',
    description: 'At the neuromuscular junction; triggers muscle contraction',
  },
  M1: {
    id: 'M1',
    displayName: 'Muscarinic M1',
    description: 'Cortical and ganglionic; memory and cognition',
  },
  M2: {
    id: 'M2',
    displayName: 'Muscarinic M2',
    description: 'Cardiac; parasympathetic slowing of the heart',
  },
  '5HT1A': {
    id: '5HT1A',
    displayName: 'Serotonin 5-HT1A',
    description: 'Inhibitory autoreceptor and postsynaptic receptor; mood regulation',
  },
  '5HT2A': {
    id: '5HT2A',
    displayName: 'Serotonin 5-HT2A',
    description: 'Excitatory GPCR; primary target of classic psychedelics',
  },
  '5HT2C': {
    id: '5HT2C',
    displayName: 'Serotonin 5-HT2C',
    description: 'Appetite, mood, and dopamine modulation',
  },
  '5HT3': {
    id: '5HT3',
    displayName: 'Serotonin 5-HT3',
    description: 'Ligand-gated channel; nausea and vomiting, gut motility',
  },
  D1: {
    id: 'D1',
    displayName: 'Dopamine D1',
    description: 'Excitatory GPCR; cortical and striatal signalling',
  },
  D2: {
    id: 'D2',
    displayName: 'Dopamine D2',
    description: 'Inhibitory GPCR; reward, movement, antipsychotic target',
  },
  D3: {
    id: 'D3',
    displayName: 'Dopamine D3',
    description: 'Limbic; motivation and reward salience',
  },
  ALPHA1: {
    id: 'ALPHA1',
    displayName: 'Adrenergic α1',
    description: 'Vascular smooth muscle; constricts blood vessels',
  },
  ALPHA2: {
    id: 'ALPHA2',
    displayName: 'Adrenergic α2',
    description: 'Presynaptic autoreceptor; dampens noradrenaline release',
  },
  BETA1: {
    id: 'BETA1',
    displayName: 'Adrenergic β1',
    description: 'Cardiac; increases heart rate and force',
  },
  BETA2: {
    id: 'BETA2',
    displayName: 'Adrenergic β2',
    description: 'Airway and vascular smooth muscle; bronchodilation',
  },
  H1: {
    id: 'H1',
    displayName: 'Histamine H1',
    description: 'Wakefulness and allergy; blockade causes drowsiness',
  },
  SERT: {
    id: 'SERT',
    displayName: 'Serotonin transporter',
    description: 'Clears serotonin from the synapse; SSRI target',
  },
  DAT: {
    id: 'DAT',
    displayName: 'Dopamine transporter',
    description: 'Clears dopamine from the synapse; cocaine and stimulant target',
  },
  NET: {
    id: 'NET',
    displayName: 'Noradrenaline transporter',
    description: 'Clears noradrenaline; target of stimulants and some antidepressants',
  },
  MOR: {
    id: 'MOR',
    displayName: 'μ-opioid',
    description: 'Analgesia, euphoria, respiratory depression, gut slowing',
  },
  KOR: {
    id: 'KOR',
    displayName: 'κ-opioid',
    description: 'Analgesia with dysphoria and sedation',
  },
  DOR: {
    id: 'DOR',
    displayName: 'δ-opioid',
    description: 'Analgesia and mood modulation',
  },
  CB1: {
    id: 'CB1',
    displayName: 'Cannabinoid CB1',
    description: 'Most abundant GPCR in the CNS; modulates transmitter release',
  },
  CB2: {
    id: 'CB2',
    displayName: 'Cannabinoid CB2',
    description: 'Mainly immune cells and peripheral tissue; inflammation',
  },
  MT1: {
    id: 'MT1',
    displayName: 'Melatonin MT1',
    description: 'Suppresses wake-promoting neuronal firing',
  },
  MT2: {
    id: 'MT2',
    displayName: 'Melatonin MT2',
    description: 'Circadian phase-shifting',
  },
  GR: {
    id: 'GR',
    displayName: 'Glucocorticoid receptor',
    description: 'Nuclear receptor; stress response and energy mobilization',
  },
};

export const RECEPTOR_IDS = Object.keys(RECEPTORS) as ReceptorId[];

/**
 * The single source of truth for which receptor activity affects which
 * structure. Substance files declare receptor bindings only; the regions that
 * light up are always derived from this map, never hand-specified per
 * substance. That is what keeps regional effects consistent as the substance
 * library grows.
 *
 * These are simplified principal sites, not exhaustive expression maps — most
 * of these receptors are expressed far more widely than the few entries listed.
 */
export const RECEPTOR_TO_REGIONS: Record<ReceptorId, RegionId[]> = {
  A1: ['prefrontal_cortex', 'thalamus', 'hippocampus'],
  A2A: ['striatum', 'nucleus_accumbens'],

  GABA_A: ['cerebellum', 'prefrontal_cortex', 'hippocampus', 'thalamus', 'spinal_dorsal_horn'],
  GABA_B: ['thalamus', 'spinal_ventral_horn', 'brain_stem'],
  NMDA: ['hippocampus', 'prefrontal_cortex', 'spinal_dorsal_horn'],
  NAV: ['peripheral_sensory', 'spinal_dorsal_horn'],

  nAChR: ['prefrontal_cortex', 'thalamus', 'vta', 'autonomic_ganglia'],
  nAChR_MUSCLE: ['neuromuscular_junction'],
  M1: ['prefrontal_cortex', 'hippocampus', 'autonomic_ganglia'],
  M2: ['vagus_nerve', 'brain_stem'],

  '5HT1A': ['raphe_nuclei', 'prefrontal_cortex', 'amygdala'],
  '5HT2A': ['visual_cortex', 'prefrontal_cortex', 'thalamus'],
  '5HT2C': ['hypothalamus', 'striatum'],
  '5HT3': ['enteric_plexus', 'brain_stem', 'vagus_nerve'],

  D1: ['prefrontal_cortex', 'striatum', 'nucleus_accumbens'],
  D2: ['striatum', 'nucleus_accumbens', 'vta'],
  D3: ['nucleus_accumbens', 'vta'],

  ALPHA1: ['sympathetic_chain', 'locus_coeruleus'],
  ALPHA2: ['locus_coeruleus', 'sympathetic_chain', 'spinal_dorsal_horn'],
  BETA1: ['sympathetic_chain'],
  BETA2: ['sympathetic_chain', 'peripheral_sensory'],
  H1: ['hypothalamus', 'thalamus', 'prefrontal_cortex'],

  SERT: ['raphe_nuclei', 'prefrontal_cortex', 'enteric_plexus'],
  DAT: ['striatum', 'nucleus_accumbens', 'prefrontal_cortex'],
  NET: ['locus_coeruleus', 'prefrontal_cortex', 'sympathetic_chain'],

  MOR: ['brain_stem', 'nucleus_accumbens', 'spinal_dorsal_horn', 'enteric_plexus'],
  KOR: ['spinal_dorsal_horn', 'striatum', 'hypothalamus'],
  DOR: ['spinal_dorsal_horn', 'prefrontal_cortex'],

  CB1: ['hippocampus', 'cerebellum', 'prefrontal_cortex', 'enteric_plexus'],
  CB2: ['peripheral_sensory', 'enteric_plexus'],

  MT1: ['hypothalamus'],
  MT2: ['hypothalamus', 'brain_stem'],

  GR: ['amygdala', 'hippocampus', 'hypothalamus'],
};
